import { motion } from "motion/react";
import { MouseEvent, ReactNode, useCallback, useState } from "react";
import { TbHeartHandshake, TbUser } from "react-icons/tb";

import { getPassengerLanguageLabel } from "@/helpers/passengerLanguage";
import { IPlanPassenger } from "@/types/tasks.types";

import { PassengerPhotoDialog } from "./dialogs/PassengerPhotoDialog";

export const PassengerContent = ({
    passenger,
    onClick,
    hasEditPermission,
    isArrived,
    children,
}: {
    passenger: IPlanPassenger<"api">;
    taskPending?: boolean;
    onClick?: () => void;
    hasEditPermission?: boolean;
    isArrived?: boolean;
    children: ReactNode;
}) => {
    const [photoOpen, setPhotoOpen] = useState(false);

    const handleClick = useCallback(() => {
        if (hasEditPermission && onClick) {
            onClick();
        }
    }, [hasEditPermission, onClick]);

    const handlePhotoClick = useCallback((e: MouseEvent) => {
        e.stopPropagation();
        setPhotoOpen(true);
    }, []);

    const hasPhoto = !!passenger.headPhotoUrl;
    const languageLabel = getPassengerLanguageLabel(passenger.languages?.[0]);

    return (
        <div
            className={`flex flex-col gap-2 p-3 w-full  rounded-sm shadow shadow-black/20 cursor-pointer ${!isArrived ? "bg-white" : "bg-green-200 text-green-900"}`}
            onClick={handleClick}
        >
            <div className="flex justify-between">
                <div className={`flex items-center gap-2  font-medium  `}>
                    <motion.button
                        type="button"
                        whileTap={hasPhoto ? { scale: 0.9 } : undefined}
                        onClick={hasPhoto ? handlePhotoClick : undefined}
                        className={`w-8 h-8 rounded-full overflow-hidden flex items-center justify-center shrink-0 ring-1 ${
                            isArrived ? "ring-green-300/70 bg-green-100" : "ring-elitLightBlue/40 bg-elitLightBlue/15"
                        } ${hasPhoto ? "cursor-zoom-in" : "cursor-default"}`}
                    >
                        {hasPhoto ? (
                            <img
                                src={passenger.headPhotoUrl as string}
                                alt={passenger.name}
                                className="w-full h-full object-cover"
                            />
                        ) : (
                            <TbUser className="text-sm opacity-70" />
                        )}
                    </motion.button>
                    <span>{passenger.name}</span>
                </div>
                {children}
            </div>

            {passenger.companions.length > 0 && (
                <div
                    className={`flex items-center gap-1 p-1 px-2 rounded-xl w-fit max-w-[200px] border   ${!isArrived ? "bg-elitLightBlue/15 border-blue-200" : " bg-green-00  text-green-900 border-green-300"}`}
                >
                    <TbHeartHandshake />
                    <span className="text-[11px] font-medium  text-ellipsis whitespace-nowrap overflow-hidden  ">
                        {passenger.companions.join(", ")}
                    </span>
                </div>
            )}

            <PassengerPhotoDialog
                open={photoOpen}
                onClose={() => setPhotoOpen(false)}
                photoUrl={passenger.headPhotoUrl}
                name={passenger.name}
                languageLabel={languageLabel}
            />
        </div>
    );
};
